import { z } from 'zod'

export const LEARNING_CREDENTIALS_CONTRACT_VERSION = '1.0.0' as const
export const LEARNING_CREDENTIALS_SCHEMA_VERSION = 'learning-credentials-v1' as const

const text = z.string().trim().min(1)
const timestamp = z.string().datetime({ offset: true })
const nullableTimestamp = timestamp.nullable()
const credentialType = z.enum(['participation', 'completion'])
const awardStatus = z.enum(['pending_review', 'review_rejected', 'pending_generation', 'generating', 'generation_failed', 'issued', 'revoked', 'reissued', 'superseded'])
const reviewStatus = z.enum(['not_required', 'pending', 'approved', 'rejected'])
const deliveryStatus = z.enum(['pending', 'sent', 'failed', 'unavailable'])

export const learningCredentialAwardSchema = z.object({
  id: text,
  learnerId: text,
  publicationId: text,
  enrollmentId: text,
  evaluationId: text,
  programId: text,
  programVersionId: text,
  programAchievementId: text,
  credentialType,
  awardCycle: z.number().int().positive(),
  status: awardStatus,
  reviewStatus: reviewStatus,
  displayName: text,
  programTitle: text,
  programVersion: text,
  issuerId: text,
  issuerName: text,
  policySnapshot: z.record(z.unknown()),
  definitionHash: text,
  evidenceIds: z.array(text),
  decisionProvenanceHash: text.nullable(),
  completedAt: timestamp,
  issuedAt: nullableTimestamp,
  expiresAt: nullableTimestamp,
  verificationId: text,
  artifactKey: text.nullable(),
  artifactSha256: z.string().regex(/^sha256:[a-f0-9]{64}$/u).nullable(),
  artifactSizeBytes: z.number().int().nonnegative().nullable(),
  generationAttempts: z.number().int().nonnegative(),
  generationError: z.string().nullable(),
  generatedAt: nullableTimestamp,
  revokedAt: nullableTimestamp,
  revokedBy: text.nullable(),
  revocationReason: z.string().nullable(),
  supersedesAwardId: text.nullable(),
  supersededByAwardId: text.nullable(),
  createdAt: timestamp,
  updatedAt: timestamp,
}).strict()

export const learningLearnerAwardListResponseSchema = z.object({ items: z.array(learningCredentialAwardSchema) }).strict()
export const learningLearnerAwardResponseSchema = learningCredentialAwardSchema
export const learningLearnerAwardResendResponseSchema = z.object({
  award: learningCredentialAwardSchema,
  delivery: z.object({
    id: text,
    awardId: text,
    learnerId: text,
    idempotencyKey: text,
    destinationKind: z.literal('email'),
    destinationFingerprint: z.string().nullable(),
    status: deliveryStatus,
    providerMessageId: z.string().nullable(),
    error: z.string().nullable(),
    attemptCount: z.number().int().nonnegative(),
    requestedAt: timestamp,
    sentAt: nullableTimestamp,
    updatedAt: timestamp,
  }).strict(),
}).strict()

export const learningCredentialAdminAwardResponseSchema = z.object({ award: learningCredentialAwardSchema }).strict()

export const learningCredentialAdminAwardListQuerySchema = z.object({
  reviewStatus: reviewStatus.optional(),
  status: awardStatus.optional(),
  programId: text.max(180).optional(),
  memberProfileId: text.max(180).optional(),
  learnerEmail: z.string().trim().email().max(320).optional(),
  limit: z.string().optional(),
  offset: z.string().optional(),
}).strict()

export const learningCredentialAdminAwardListRowSchema = z.object({
  id: text,
  learnerId: text,
  displayName: text,
  programId: text,
  programTitle: text,
  status: awardStatus,
  reviewStatus,
  issuedAt: nullableTimestamp,
  revokedAt: nullableTimestamp,
  credentialType,
  verificationId: text,
}).strict()

export const learningCredentialAdminAwardListResponseSchema = z.object({
  items: z.array(learningCredentialAdminAwardListRowSchema),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
}).strict()

export const learningCredentialAwardAuditSchema = z.object({
  id: text,
  awardId: text,
  actorId: text,
  action: text,
  priorStatus: z.string().nullable(),
  newStatus: text,
  reason: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: timestamp,
}).strict()

export const learningCredentialAdminAwardDetailResponseSchema = z.object({
  award: learningCredentialAwardSchema,
  auditTrail: z.array(learningCredentialAwardAuditSchema),
}).strict()

export const learningCredentialVerificationResponseSchema = z.object({
  verificationId: text,
  status: awardStatus,
  valid: z.boolean(),
  reviewStatus,
  artifactSha256: z.string().regex(/^sha256:[a-f0-9]{64}$/u).nullable(),
  issuerId: text,
  issuerName: text,
  credentialType,
  issuedAt: nullableTimestamp,
  expiresAt: nullableTimestamp,
}).strict()

export const learningCredentialReviewRequestSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  reason: text.max(1000),
}).strict()
export const learningCredentialReasonRequestSchema = z.object({ reason: text.max(1000) }).strict()
export const learningCredentialEmptyRequestSchema = z.object({}).strict()
export const learningCredentialResendRequestSchema = z.object({ idempotencyKey: text.max(200).optional() }).strict()

export type LearningCredentialAdminAwardListQuery = z.infer<typeof learningCredentialAdminAwardListQuerySchema>
export type LearningCredentialAdminAwardListRow = z.infer<typeof learningCredentialAdminAwardListRowSchema>
export type LearningCredentialAdminAwardListResponse = z.infer<typeof learningCredentialAdminAwardListResponseSchema>
export type LearningCredentialAwardAudit = z.infer<typeof learningCredentialAwardAuditSchema>
export type LearningCredentialAdminAwardDetailResponse = z.infer<typeof learningCredentialAdminAwardDetailResponseSchema>
export type LearningCredentialAward = z.infer<typeof learningCredentialAwardSchema>
export type LearningLearnerAwardListResponse = z.infer<typeof learningLearnerAwardListResponseSchema>
export type LearningLearnerAwardResponse = z.infer<typeof learningLearnerAwardResponseSchema>
export type LearningLearnerAwardResendResponse = z.infer<typeof learningLearnerAwardResendResponseSchema>
export type LearningCredentialAdminAwardResponse = z.infer<typeof learningCredentialAdminAwardResponseSchema>
export type LearningCredentialVerificationResponse = z.infer<typeof learningCredentialVerificationResponseSchema>
export type LearningCredentialReviewRequest = z.infer<typeof learningCredentialReviewRequestSchema>
export type LearningCredentialReasonRequest = z.infer<typeof learningCredentialReasonRequestSchema>
